import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash,
    faEdit,
    faSync,
    faSearch,
    faFileContract,
    faTimes,
    faEye,
    faCheckCircle,
    faExclamationCircle,
    faUpload,
    faImage
} from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import baseURL from '../../url';
import NewPolitica from '../NewPolitica/NewPolitica';
import './PoliticasData.css';

export default function PoliticasData() {
    const [politicas, setPoliticas] = useState([]);
    const [loading, setLoading] = useState(true);

    // Filtros
    const [filtroTexto, setFiltroTexto] = useState('');
    const [filtroCategoria, setFiltroCategoria] = useState('todas');
    const [filtroEstado, setFiltroEstado] = useState('todos');

    // Modal Editar
    const [modalEditar, setModalEditar] = useState(false);
    const [politicaEditando, setPoliticaEditando] = useState(null);
    const [editTitulo, setEditTitulo] = useState('');
    const [editCategoria, setEditCategoria] = useState('General');
    const [editOtraCategoria, setEditOtraCategoria] = useState('');
    const [editDescripcion, setEditDescripcion] = useState('');
    const [editEstado, setEditEstado] = useState('activo');
    const [editOrden, setEditOrden] = useState('0');
    const [editImagen, setEditImagen] = useState(null);
    const [editImagenPreview, setEditImagenPreview] = useState(null);
    const [editImagenActual, setEditImagenActual] = useState(null);
    const [eliminarImagen, setEliminarImagen] = useState(false);
    const [actualizando, setActualizando] = useState(false);

    // Modal Ver Detalle
    const [modalDetalle, setModalDetalle] = useState(false);
    const [politicaDetalle, setPoliticaDetalle] = useState(null);

    useEffect(() => {
        cargarPoliticas();
    }, []);

    const cargarPoliticas = () => {
        setLoading(true);
        fetch(`${baseURL}/politicasGet.php`, {
            method: 'GET',
            credentials: 'include'
        })
            .then(res => res.json())
            .then(data => {
                setPoliticas(data.politicas || []);
                setLoading(false);
            })
            .catch(err => {
                console.error('Error al cargar políticas:', err);
                toast.error('Error al cargar la lista de políticas');
                setLoading(false);
            });
    };

    // ==========================================
    // ELIMINAR POLÍTICA
    // ==========================================
    const eliminarPolitica = (idPolitica, titulo) => {
        Swal.fire({
            title: '¿Estás seguro?',
            text: `Se eliminará la política "${titulo || 'seleccionada'}". ¡Esta acción no se puede deshacer!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#e11d48',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar',
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(`${baseURL}/politicaDelete.php?idPolitica=${idPolitica}`, {
                    method: 'DELETE',
                    credentials: 'include',
                })
                    .then(res => res.json())
                    .then(data => {
                        if (data.mensaje) {
                            Swal.fire('¡Eliminado!', data.mensaje, 'success');
                            setPoliticas(prev => prev.filter(p => p.idPolitica !== idPolitica));
                        } else {
                            Swal.fire('Error', data.error || 'No se pudo eliminar la política', 'error');
                        }
                    })
                    .catch(err => {
                        console.error('Error:', err);
                        toast.error('Error de conexión al eliminar la política');
                    });
            }
        });
    };

    // ==========================================
    // EDITAR POLÍTICA
    // ==========================================
    const abrirModalEditar = (item) => {
        setPoliticaEditando(item);
        setEditTitulo(item.titulo || '');

        const categoriasPredefinidas = [
            'General',
            'Términos y Condiciones',
            'Protección de Datos',
            'Protección al Menor',
            'Cobro, Cancelación y Reembolso',
            'Cancelación y Reembolso',
            'Envíos',
            'Reservas',
            'Privacidad',
            'Cookies'
        ];

        if (categoriasPredefinidas.includes(item.categoria)) {
            setEditCategoria(item.categoria);
            setEditOtraCategoria('');
        } else {
            setEditCategoria('Otra');
            setEditOtraCategoria(item.categoria || '');
        }

        setEditDescripcion(item.descripcion || '');
        setEditEstado(item.estado || 'activo');
        setEditOrden(item.orden ? String(item.orden) : '0');
        setEditImagen(null);
        setEditImagenPreview(null);
        setEditImagenActual(item.imagen || null);
        setEliminarImagen(false);
        setModalEditar(true);
    };

    const cerrarModalEditar = () => {
        setModalEditar(false);
        setPoliticaEditando(null);
        setEditImagen(null);
        setEditImagenPreview(null);
        setEditImagenActual(null);
        setEliminarImagen(false);
        setActualizando(false);
    };

    const handleEditImagenChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEditImagen(file);
            setEditImagenPreview(URL.createObjectURL(file));
            setEliminarImagen(false);
        }
    };

    const handleQuitarEditImagen = () => {
        setEditImagen(null);
        setEditImagenPreview(null);
        setEditImagenActual(null);
        setEliminarImagen(true);
    };

    const handleActualizar = async (e) => {
        if (e) e.preventDefault();
        if (!politicaEditando) return;

        if (!editTitulo.trim()) {
            toast.error('El título es obligatorio.');
            return;
        }

        if (!editDescripcion.trim()) {
            toast.error('La descripción es obligatoria.');
            return;
        }

        const categoriaFinal = editCategoria === 'Otra'
            ? (editOtraCategoria.trim() || 'General')
            : editCategoria;

        setActualizando(true);

        const formData = new FormData();
        formData.append('idPolitica', politicaEditando.idPolitica);
        formData.append('titulo', editTitulo.trim());
        formData.append('categoria', categoriaFinal);
        formData.append('descripcion', editDescripcion.trim());
        formData.append('estado', editEstado);
        formData.append('orden', parseInt(editOrden, 10) || 0);
        if (editImagen) {
            formData.append('imagen', editImagen);
        }
        if (eliminarImagen) {
            formData.append('eliminarImagen', '1');
        }

        try {
            const response = await fetch(`${baseURL}/politicaPut.php?idPolitica=${politicaEditando.idPolitica}`, {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            const data = await response.json();

            if (data.mensaje) {
                toast.success(data.mensaje);
                cerrarModalEditar();
                cargarPoliticas();
            } else {
                toast.error(data.error || 'No se pudo actualizar la política');
            }
        } catch (error) {
            console.error('Error al actualizar política:', error);
            toast.error('Error de conexión al actualizar.');
        } finally {
            setActualizando(false);
        }
    };

    // ==========================================
    // VER DETALLE
    // ==========================================
    const abrirDetalle = (item) => {
        setPoliticaDetalle(item);
        setModalDetalle(true);
    };

    const cerrarDetalle = () => {
        setModalDetalle(false);
        setPoliticaDetalle(null);
    };

    // ==========================================
    // FILTRADO
    // ==========================================
    const categoriasUnicas = ['todas', ...Array.from(new Set(politicas.map(p => p.categoria).filter(Boolean)))];

    const politicasFiltradas = politicas.filter((item) => {
        const matchesTexto = (item.titulo || '').toLowerCase().includes(filtroTexto.toLowerCase()) ||
            (item.descripcion || '').toLowerCase().includes(filtroTexto.toLowerCase()) ||
            (item.categoria || '').toLowerCase().includes(filtroTexto.toLowerCase());

        const matchesCategoria = filtroCategoria === 'todas' || item.categoria === filtroCategoria;
        const matchesEstado = filtroEstado === 'todos' || item.estado === filtroEstado;

        return matchesTexto && matchesCategoria && matchesEstado;
    });

    return (
        <div className="politicas-wrapper">
            <ToastContainer />

            {/* Encabezado */}
            <div className="politicas-header-bar">
                <div className="politicas-header-title">
                    <h2>
                        <FontAwesomeIcon icon={faFileContract} style={{ color: 'var(--color1, #3F1D76)' }} />
                        Gestión de Políticas
                    </h2>
                    <span className="politicas-count-badge">
                        {politicas.length} {politicas.length === 1 ? 'política' : 'políticas'}
                    </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <button
                        className="politicas-btn-refresh"
                        onClick={cargarPoliticas}
                        title="Actualizar listado"
                        type="button"
                    >
                        <FontAwesomeIcon icon={faSync} />
                    </button>
                    <NewPolitica onPoliticaCreada={cargarPoliticas} />
                </div>
            </div>

            {/* Tabla de Políticas */}
            {loading ? (
                <div className="politicas-loading-state">
                    <FontAwesomeIcon icon={faSync} spin size="2x" style={{ color: 'var(--color1, #3F1D76)' }} />
                    <p style={{ marginTop: '10px' }}>Cargando políticas...</p>
                </div>
            ) : politicasFiltradas.length === 0 ? (
                <div className="politicas-empty-state">
                    <h4>No se encontraron políticas</h4>
                    <p>No hay políticas que coincidan con los filtros aplicados o aún no has creado ninguna.</p>
                </div>
            ) : (
                <div className="table-container">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="th-id">#</th>
                                <th className="th-img">Imagen</th>
                                <th className="th-titulo">Título</th>
                                <th className="th-acciones">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {politicasFiltradas.map((item, index) => (
                                <tr key={item.idPolitica}>
                                    <td className="politica-col-id"><strong>#{item.idPolitica}</strong></td>
                                    <td className="politica-col-img">
                                        {item.imagen ? (
                                            <img
                                                src={item.imagen}
                                                alt={item.titulo}
                                                className="politica-table-thumb"
                                            />
                                        ) : (
                                            <span className="politica-no-img">Sin imagen</span>
                                        )}
                                    </td>
                                    <td className="politica-col-titulo">
                                        <div className="politica-titulo-text">
                                            {item.titulo}
                                        </div>
                                    </td>
                                    <td className="politica-col-acciones">
                                        <div className="acciones-politica">
                                            <button
                                                className="btn-action-view"
                                                onClick={() => abrirDetalle(item)}
                                                title="Ver detalle"
                                                type="button"
                                            >
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                            <button
                                                className="editar"
                                                onClick={() => abrirModalEditar(item)}
                                                title="Editar política"
                                                type="button"
                                            >
                                                <FontAwesomeIcon icon={faEdit} />
                                            </button>
                                            <button
                                                className="eliminar"
                                                onClick={() => eliminarPolitica(item.idPolitica, item.titulo)}
                                                title="Eliminar política"
                                                type="button"
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal Editar */}
            {modalEditar && politicaEditando && (
                <div className="modal">
                    <div className="modal-content modal-content-politica">
                        <div className="deFlexBack">
                            <h4>Editar Política</h4>
                            <span className="close" onClick={cerrarModalEditar} role="button" aria-label="Cerrar">
                                <FontAwesomeIcon icon={faTimes} />
                            </span>
                        </div>

                        <form onSubmit={handleActualizar} className="politica-form-grid">
                            <fieldset>
                                <legend>Título de la Política *</legend>
                                <input
                                    type="text"
                                    value={editTitulo}
                                    onChange={(e) => setEditTitulo(e.target.value)}
                                    required
                                />
                            </fieldset>

                            <div className="politica-form-row">
                                <fieldset>
                                    <legend>Categoría / Tipo</legend>
                                    <select
                                        className="politica-select"
                                        value={editCategoria}
                                        onChange={(e) => setEditCategoria(e.target.value)}
                                    >
                                        <option value="General">General</option>
                                        <option value="Términos y Condiciones">Términos y Condiciones</option>
                                        <option value="Protección de Datos">Protección de Datos</option>
                                        <option value="Protección al Menor">Protección al Menor</option>
                                        <option value="Cobro, Cancelación y Reembolso">Cobro, Cancelación y Reembolso</option>
                                        <option value="Cancelación y Reembolso">Cancelación y Reembolso</option>
                                        <option value="Envíos">Envíos</option>
                                        <option value="Reservas">Reservas</option>
                                        <option value="Privacidad">Privacidad</option>
                                        <option value="Cookies">Cookies</option>
                                        <option value="Otra">Otra (personalizada)...</option>
                                    </select>
                                </fieldset>

                                <fieldset>
                                    <legend>Estado</legend>
                                    <select
                                        className="politica-select"
                                        value={editEstado}
                                        onChange={(e) => setEditEstado(e.target.value)}
                                    >
                                        <option value="activo">Activo (Visible)</option>
                                        <option value="inactivo">Inactivo (Borrador)</option>
                                    </select>
                                </fieldset>

                                <fieldset>
                                    <legend>Orden</legend>
                                    <input
                                        type="number"
                                        min="0"
                                        value={editOrden}
                                        onChange={(e) => setEditOrden(e.target.value)}
                                    />
                                </fieldset>
                            </div>

                            {editCategoria === 'Otra' && (
                                <fieldset>
                                    <legend>Nombre de la Categoría Personalizada *</legend>
                                    <input
                                        type="text"
                                        value={editOtraCategoria}
                                        onChange={(e) => setEditOtraCategoria(e.target.value)}
                                        required
                                    />
                                </fieldset>
                            )}

                            <fieldset className="politica-fieldset-imagen">
                                <legend>
                                    <FontAwesomeIcon icon={faImage} style={{ marginRight: '6px' }} />
                                    Imagen / Afiche Adjunto (Opcional)
                                </legend>
                                <div className="politica-upload-wrapper">
                                    <div className="politica-upload-actions">
                                        <input
                                            type="file"
                                            id="politica-file-edit"
                                            accept="image/*"
                                            onChange={handleEditImagenChange}
                                            style={{ display: 'none' }}
                                        />
                                        <label htmlFor="politica-file-edit" className="politica-upload-btn">
                                            <FontAwesomeIcon icon={faUpload} /> {(editImagenPreview || editImagenActual) ? 'Cambiar Imagen' : 'Seleccionar Imagen'}
                                        </label>
                                        <span className="politica-upload-hint">
                                            {editImagen ? editImagen.name : (editImagenActual ? 'Imagen asignada actualmente' : 'Formato JPG, PNG, WEBP')}
                                        </span>
                                    </div>
                                    {(editImagenPreview || editImagenActual) && (
                                        <div className="politica-preview-container">
                                            <img
                                                src={editImagenPreview || editImagenActual}
                                                alt="Vista previa"
                                                className="politica-preview-img"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleQuitarEditImagen}
                                                className="politica-remove-img-btn"
                                                title="Quitar imagen"
                                            >
                                                <FontAwesomeIcon icon={faTrash} /> Quitar Imagen
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </fieldset>

                            <fieldset>
                                <legend>Contenido / Descripción de la Política *</legend>
                                <textarea
                                    className="politica-textarea"
                                    rows="8"
                                    value={editDescripcion}
                                    onChange={(e) => setEditDescripcion(e.target.value)}
                                    required
                                />
                            </fieldset>

                            {actualizando ? (
                                <button type="button" className="btnLoading" disabled>
                                    Guardando cambios...
                                </button>
                            ) : (
                                <button type="submit" className="btnPost">
                                    Actualizar Política
                                </button>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Ver Detalle */}
            {modalDetalle && politicaDetalle && (
                <div className="modal">
                    <div className="modal-content modal-content-politica">
                        <div className="deFlexBack">
                            <h4>{politicaDetalle.titulo}</h4>
                            <span className="close" onClick={cerrarDetalle} role="button" aria-label="Cerrar">
                                <FontAwesomeIcon icon={faTimes} />
                            </span>
                        </div>

                        <div className="modal-detail-meta">
                            <span className="badge-categoria">
                                {politicaDetalle.categoria || 'General'}
                            </span>
                            <span className={`badge-estado ${politicaDetalle.estado || 'activo'}`}>
                                <span className="estado-dot"></span>
                                {politicaDetalle.estado === 'activo' ? 'Activo' : 'Inactivo'}
                            </span>
                            {politicaDetalle.updatedAt && (
                                <span className="modal-detail-date">
                                    Última modificación: {politicaDetalle.updatedAt}
                                </span>
                            )}
                        </div>

                        {politicaDetalle.imagen && (
                            <div style={{ textAlign: 'center', margin: '14px 0', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                                <img
                                    src={politicaDetalle.imagen}
                                    alt={politicaDetalle.titulo}
                                    style={{ maxWidth: '100%', maxHeight: '280px', objectFit: 'contain', borderRadius: '8px' }}
                                />
                            </div>
                        )}

                        <div className="modal-detail-body">
                            {politicaDetalle.descripcion}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                className="btn-action-view"
                                onClick={() => {
                                    cerrarDetalle();
                                    abrirModalEditar(politicaDetalle);
                                }}
                            >
                                <FontAwesomeIcon icon={faEdit} /> Editar
                            </button>
                            <button
                                type="button"
                                className="btnPost"
                                style={{ width: 'auto', padding: '8px 18px' }}
                                onClick={cerrarDetalle}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
